"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChimeRemoteVideoTrack = exports.ChimeLocalVideoTrack = exports.ChimeVideoTrack = void 0;
const streem_logger_1 = require("../util/streem_logger");
const toolbox_1 = require("@streem/toolbox");
const rxjs_1 = require("rxjs");
const chime_codec_helper_1 = require("../util/chime_codec_helper");
const constants_1 = require("../constants");
class ChimeVideoTrack {
    constructor(audioVideo, externalUserId) {
        this.startedSubject = new rxjs_1.BehaviorSubject(false);
        this.enabledSubject = new rxjs_1.BehaviorSubject(false);
        this.backgroundedSubject = new rxjs_1.BehaviorSubject(false);
        this.kind = 'videoTrack';
        this.enabled = this.enabledSubject.asObservable();
        this.started = this.startedSubject.asObservable();
        this.backgrounded = this.backgroundedSubject.asObservable();
        this.logger = streem_logger_1.streemLogger.extend('sdk-chime-video:ChimeVideoTrack');
        this.videoElement = document.createElement('video');
        this.buffer = document.createElement('canvas');
        this.videoElementManager = new toolbox_1.VideoElementManager(this.videoElement);
        this.audioVideo = audioVideo;
        this.externalUserId = externalUserId;
    }
    get videoTrackElement() {
        return this.videoElement;
    }
    scheduleRenderingCallback(callback) {
        const stopAnimationLoop = (0, toolbox_1.startAnimationLoop)(() => {
            // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/readyState
            if (this.videoElement.readyState < this.videoElement.HAVE_CURRENT_DATA) {
                this.logger.info(`Video element is not in a readyState: ${this.videoElement.readyState}`);
                return;
            }
            // Sanity check that our video has some resolution greater than 0x0.
            if (this.videoElement.videoHeight <= 0 || this.videoElement.videoWidth <= 0) {
                this.logger.info('Video element is 0x0px', {
                    width: this.videoElement.videoWidth,
                    height: this.videoElement.videoHeight,
                });
                return;
            }
            if (this.buffer.width !== this.videoElement.videoWidth ||
                this.buffer.height !== this.videoElement.videoHeight) {
                this.buffer.width = this.videoElement.videoWidth;
                this.buffer.height = this.videoElement.videoHeight;
            }
            callback(this.videoElement);
        }, this.videoElement);
        return () => {
            stopAnimationLoop();
        };
    }
    setBackgrounded(backgrounded) {
        this.logger.debug(`Remote video track backgrounded changed to ${backgrounded}`);
        this.backgroundedSubject.next(backgrounded);
    }
}
exports.ChimeVideoTrack = ChimeVideoTrack;
class LocalAudioVideoObserverImpl {
    constructor(localVideoElement, audioVideo) {
        this.localVideoElement = localVideoElement;
        this.audioVideo = audioVideo;
        this.currentTileId = undefined;
        this.logger = streem_logger_1.streemLogger.extend('sdk-chime-video:LocalAudioVideoObserver');
    }
    videoTileDidUpdate(tileState) {
        (0, toolbox_1.invariant)(tileState.tileId, 'Tile id is not available');
        if (tileState.localTile && !tileState.boundVideoElement) {
            this.audioVideo.bindVideoElement(tileState.tileId, this.localVideoElement);
            this.currentTileId = tileState.tileId;
            this.logger.info(`Local video tile updated: ${tileState.tileId}`);
        }
    }
    videoTileWasRemoved(tileId) {
        if (tileId === this.currentTileId) {
            this.logger.debug(`Local video tile removed: ${tileId}`);
            this.audioVideo.unbindVideoElement(tileId);
            this.currentTileId = undefined;
        }
    }
}
class ChimeLocalVideoTrack extends ChimeVideoTrack {
    constructor(audioVideo, externalUserId, preferredVideoCodecs) {
        super(audioVideo, externalUserId);
        this.inputDevice = undefined;
        this.videoMediaStream = undefined;
        this.preferredVideoCodecs = preferredVideoCodecs;
    }
    getMediaTrackConstraints() {
        (0, toolbox_1.invariant)(this.videoMediaStream, 'Video media stream is not available');
        return this.videoMediaStream.getVideoTracks()[0].getConstraints();
    }
    restart(constraints) {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.info(`Restarting local video track trackLabel=${this.trackLabel}`);
            (0, toolbox_1.invariant)(constraints, 'constraints should not be undefined');
            try {
                if (this.videoMediaStream) {
                    this.videoMediaStream.getVideoTracks().forEach(videoTrack => {
                        videoTrack.stop();
                    });
                }
                this.videoMediaStream = yield this.audioVideo.startVideoInput(constraints);
                (0, toolbox_1.invariant)(this.videoMediaStream, 'Video media stream is not available after restart');
                const videoTracks = this.videoMediaStream.getVideoTracks();
                (0, toolbox_1.invariant)(videoTracks.length > 0, 'Video media stream is not available after attempting restart');
                this.trackLabel = videoTracks[0].label;
                return true;
            }
            catch (error) {
                this.logger.error(`Failed to restart local video track ${this.trackLabel}`, error);
                return false;
            }
        });
    }
    setEnabled(enable) {
        if (enable) {
            this.audioVideo.startLocalVideoTile();
            this.enabledSubject.next(true);
        }
        else {
            this.audioVideo.stopLocalVideoTile();
            this.enabledSubject.next(false);
        }
    }
    initializeVideoTrack(videoConstraints, videoInputDevices) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(videoConstraints, 'videoConstraints should not be false or undefined');
            if (this.audioVideo.setVideoCodecSendPreferences) {
                const videoCapabilities = (0, chime_codec_helper_1.convertPreferredVideoCodecsToVideoCodecCapabilities)(this.preferredVideoCodecs);
                this.logger.info('Setting preferred video codecs', JSON.stringify(videoCapabilities));
                this.audioVideo.setVideoCodecSendPreferences(videoCapabilities);
            }
            this.audioVideo.chooseVideoInputQuality(constants_1.VIDEO_WIDTH, constants_1.VIDEO_HEIGHT, constants_1.FRAME_RATE);
            this.logger.info('Input quality has been set to:', this.audioVideo.getVideoInputQualitySettings());
            this.inputDevice =
                videoConstraints === true
                    ? (yield this.findDefaultCamera(videoInputDevices)).deviceId
                    : videoConstraints;
            this.videoMediaStream = yield this.audioVideo.startVideoInput(this.inputDevice);
            (0, toolbox_1.invariant)(this.videoMediaStream, 'Video media stream is not available after initialization');
            this.startedSubject.next(true);
            this.trackLabel = this.videoMediaStream.getVideoTracks()[0].label;
            this.localAudioVideoObserver = new LocalAudioVideoObserverImpl(this.videoElement, this.audioVideo);
            this.audioVideo.addObserver(this.localAudioVideoObserver);
            this.videoElementManager.addHiddenVideoToDOM();
            this.logger.info(`LocalChimeVideoTrack with label=${this.trackLabel} created local video for user with userId: ${this.externalUserId}`);
            return true;
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.logger.info('ChimeLocalVideoTrack disconnecting the local video track');
            this.videoElementManager.removeVideoFromDOM();
            this.audioVideo.stopLocalVideoTile();
            this.audioVideo.removeLocalVideoTile();
            yield this.audioVideo.stopVideoInput();
            this.startedSubject.next(false);
            this.enabledSubject.next(false);
            this.audioVideo.removeObserver(this.localAudioVideoObserver);
            this.trackLabel = '';
            this.inputDevice = undefined;
            this.videoMediaStream = undefined;
        });
    }
    findDefaultCamera(videoInputDevices) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, toolbox_1.invariant)(videoInputDevices.length > 0, 'No video input devices found');
            const defaultDevice = videoInputDevices.find(device => device.deviceId === 'default');
            if (defaultDevice) {
                return defaultDevice;
            }
            this.logger.warn('No default video input device found, using first device');
            return videoInputDevices[0];
        });
    }
}
exports.ChimeLocalVideoTrack = ChimeLocalVideoTrack;
class ChimeRemoteVideoTrack extends ChimeVideoTrack {
    constructor(audioVideo, externalUserId) {
        super(audioVideo, externalUserId);
        this.pausedSubject = new rxjs_1.BehaviorSubject(false);
        this.paused = this.pausedSubject
            .asObservable()
            .pipe((0, rxjs_1.distinctUntilChanged)(), (0, rxjs_1.throttleTime)(3000, undefined, { trailing: true, leading: false }));
        this.onPausedChange = (paused) => {
            this.logger.debug(`Remote video track paused changed to ${paused}`);
            this.pausedSubject.next(paused);
        };
        this.onEnabledChange = (enabled) => {
            this.logger.debug(`Remote video track enabled changed to ${enabled}`);
            this.enabledSubject.next(enabled);
        };
    }
    initializeVideoTrack() {
        this.videoElementManager.addHiddenVideoToDOM();
        this.startedSubject.next(true);
        return true;
    }
    disconnect() {
        this.logger.info('ChimeRemoteVideoTrack disconnecting the remote video track');
        this.videoElementManager.removeVideoFromDOM();
        this.enabledSubject.next(false);
        this.startedSubject.next(false);
        this.trackLabel = '';
    }
}
exports.ChimeRemoteVideoTrack = ChimeRemoteVideoTrack;
//# sourceMappingURL=video_tracks.js.map